import { reduxKey } from './constants';
import {
  setCurrentFolderId,
  unsetCurrentFolderId,
  setCurrentTierId,
  unsetCurrentTierId,
  changeCurrentMediaSource,
  replaceSelect,
  toggleAppendSelect,
  deselect,
  deselectAll,
  deselectMulti,
  setSearchInput,
  setSearchQuery,
  unsetSearchQuery,
  unsetSearchInput,
  initMediaPopup,
  setCurrentSubfolderId,
  unsetCurrentSubfolderId,
  showNewFolderPopup,
  hideNewFolderPopup,
  showAddDeviationsPopup,
  hideAddDeviationsPopup,
} from './actions';
import { getDeviationEntityId, getDeviationEntityIds } from '../redux/normalizr/schemas/deviation';
import { MediaPopupStore } from './types';
import { handleActions } from 'redux-actions';

const defaultState: MediaPopupStore = {
  searchInput: '',
  selectedDeviationEntityIds: [],
  allowGifs: true,
  allowPremium: false,
  allowInTierContent: false,
  allowDefaultImage: true,
  allowMature: true,
  allowUploads: true,
  allowMultipleUploads: true,
  isShowingNewGallectionPopup: false,
  isShowingAddDeviationsPopup: false,
};

const actionHandlers = handleActions<MediaPopupStore, any>(
  {
    [initMediaPopup.toString()]: (state, action: ReturnType<typeof initMediaPopup>) => ({
      ...state,
      mode: action.payload.mode,
      currentFolderId: action.payload.gallectionFolder?.folderId,
      currentTierId: action.payload.tier?.deviationId,
      currentMediaSource: action.payload.mediaSource,
      selectionLimit: action.payload.selectionLimit,
      allowedDeviationTypes: action.payload.allowedDeviationTypes,
      allowedDeviationResourceTypes: action.payload.allowedDeviationResourceTypes,
      allowGifs: action.payload.allowGifs ?? state.allowGifs,
      allowPremium: action.payload.allowPremium ?? state.allowPremium,
      allowInTierContent: action.payload.allowInTierContent ?? state.allowInTierContent,
      allowDefaultImage: action.payload.allowDefaultImage ?? state.allowDefaultImage,
      allowMature: action.payload.allowMature ?? state.allowMature,
      allowUploads: action.payload.allowUploads ?? state.allowUploads,
      allowMultipleUploads: action.payload.allowMultipleUploads ?? state.allowMultipleUploads,
      ignoreDeviationIds: action.payload.ignoreDeviationIds,
      minDeviationWidth: action.payload.minDeviationWidth,
      minDeviationHeight: action.payload.minDeviationHeight,
      mediaSourceTypes: action.payload.mediaSourceTypes ?? state.mediaSourceTypes,
      withMediaSourceSelector:
        action.payload.withMediaSourceSelector ?? state.withMediaSourceSelector,
      isShowingNewGallectionPopup:
        action.payload.withNewGallectionPopupOpen ?? state.isShowingNewGallectionPopup,
      isShowingAddDeviationsPopup:
        action.payload.withAddDeviationsPopupOpen ?? state.isShowingNewGallectionPopup,
      gruser: action.payload.gruser,
    }),
    [showNewFolderPopup.toString()]: state => ({
      ...state,
      isShowingNewGallectionPopup: true,
    }),
    [hideNewFolderPopup.toString()]: state => ({
      ...state,
      isShowingNewGallectionPopup: false,
    }),
    [showAddDeviationsPopup.toString()]: state => ({
      ...state,
      isShowingAddDeviationsPopup: true,
    }),
    [hideAddDeviationsPopup.toString()]: state => ({
      ...state,
      isShowingAddDeviationsPopup: false,
    }),
    [setCurrentFolderId.toString()]: (state, action: ReturnType<typeof setCurrentFolderId>) => ({
      ...state,
      currentFolderId: action.payload,
    }),
    [unsetCurrentFolderId.toString()]: state => ({
      ...state,
      currentFolderId: undefined,
    }),
    [setCurrentSubfolderId.toString()]: (
      state,
      action: ReturnType<typeof setCurrentSubfolderId>
    ) => ({
      ...state,
      currentSubfolderId: action.payload,
    }),
    [unsetCurrentSubfolderId.toString()]: state => ({
      ...state,
      currentSubfolderId: undefined,
    }),
    [setCurrentTierId.toString()]: (state, action: ReturnType<typeof setCurrentTierId>) => ({
      ...state,
      currentTierId: action.payload,
    }),
    [unsetCurrentTierId.toString()]: state => ({
      ...state,
      currentTierId: undefined,
    }),
    [changeCurrentMediaSource.toString()]: (
      state,
      action: ReturnType<typeof changeCurrentMediaSource>
    ) => ({
      ...state,
      currentMediaSource: action.payload,
    }),
    [setSearchInput.toString()]: (state, action: ReturnType<typeof setSearchInput>) => ({
      ...state,
      searchInput: action.payload,
    }),
    [unsetSearchInput.toString()]: state => ({
      ...state,
      searchInput: '',
    }),
    [setSearchQuery.toString()]: (state, action: ReturnType<typeof setSearchQuery>) => ({
      ...state,
      searchQuery: action.payload.query,
    }),
    [unsetSearchQuery.toString()]: state => ({
      ...state,
      searchQuery: undefined,
    }),
    [replaceSelect.toString()]: (state, action: ReturnType<typeof replaceSelect>) => ({
      ...state,
      selectedDeviationEntityIds: [getDeviationEntityId(action.payload)],
    }),
    [toggleAppendSelect.toString()]: (state, action: ReturnType<typeof toggleAppendSelect>) => {
      const { selectedDeviationEntityIds } = state;
      const id = getDeviationEntityId(action.payload);
      const isSelected = selectedDeviationEntityIds.includes(id);
      let updatedSelectedDeviationEntityIds;
      if (isSelected) {
        updatedSelectedDeviationEntityIds = selectedDeviationEntityIds.filter(
          entityId => entityId !== id
        );
      } else {
        updatedSelectedDeviationEntityIds = [...selectedDeviationEntityIds, id];
      }
      return {
        ...state,
        selectedDeviationEntityIds: updatedSelectedDeviationEntityIds,
      };
    },
    [deselect.toString()]: (state, action: ReturnType<typeof deselect>) => {
      const id = getDeviationEntityId(action.payload);
      return {
        ...state,
        selectedDeviationEntityIds: state.selectedDeviationEntityIds.filter(
          entityId => entityId !== id
        ),
      };
    },
    [deselectMulti.toString()]: (state, action: ReturnType<typeof deselectMulti>) => {
      const ids = getDeviationEntityIds(action.payload);
      return {
        ...state,
        selectedDeviationEntityIds: state.selectedDeviationEntityIds.filter(
          entityId => !ids.includes(entityId)
        ),
      };
    },
    [deselectAll.toString()]: state => ({
      ...state,
      selectedDeviationEntityIds: [],
    }),
  },
  defaultState
);

const reducers = {
  [reduxKey as string]: actionHandlers,
};

export default reducers;
